import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Plus, Sparkles, Pin, PinOff, ShieldCheck, ArrowUpNarrowWide,
  Archive, Pencil, Loader2, Library, Layers, BadgeCheck, Tag, X, Brain,
  Inbox, CheckCircle2, XCircle, GitMerge, ClipboardCheck, Flag, Network, BarChart3,
} from "lucide-react";
import {
  API, SCOPE_CONFIG, SCOPE_ORDER, VERTICALS, CONTENT_TYPES,
  SOURCE_TYPE_LABEL, verticalLabel,
} from "./kbConstants";
import KnowledgeGraph from "./KnowledgeGraph";
import KnowledgeAnalytics from "./KnowledgeAnalytics";

const EMPTY_FORM = {
  scope: "individual:", title: "", body: "",
  content_type: "insight", vertical: "general", tags: "",
};

function ScopeBadge({ scope }) {
  const cfg = SCOPE_CONFIG[scope] || SCOPE_CONFIG.individual;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cfg.badge}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

export default function KnowledgeExplorer() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // filters
  const [query, setQuery] = useState("");
  const [semantic, setSemantic] = useState(false);
  const [fScope, setFScope] = useState("all");
  const [fVertical, setFVertical] = useState("all");
  const [fSource, setFSource] = useState("all");

  // dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);

  // review queue
  const [view, setView] = useState("library"); // library | queue
  const [queue, setQueue] = useState([]);
  const [queueCount, setQueueCount] = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const [{ data }, { data: c }] = await Promise.all([
        axios.get(`${API}/kb/queue`),
        axios.get(`${API}/kb/queue/count`),
      ]);
      setQueue(data.queue || []);
      setQueueCount(c.count || 0);
    } catch (e) { /* silent */ } finally { setQueueLoading(false); }
  }, []);

  const loadQueueCount = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/kb/queue/count`); setQueueCount(data.count || 0); }
    catch (e) { /* silent */ }
  }, []);

  const queueAction = async (fn, ok) => {
    try { await fn(); toast.success(ok); loadQueue(); loadStatic(); }
    catch (e) { toast.error(e?.response?.data?.message || "Action failed"); }
  };

  const approveAll = async () => {
    const batches = [...new Set(queue.map((q) => q.batch_id).filter(Boolean))];
    try {
      await Promise.all(batches.map((b) => axios.post(`${API}/kb/queue/approve-batch`, { batch_id: b })));
      toast.success("All artifacts approved");
      loadQueue(); loadStatic(); setView("library"); loadEntries();
    } catch (e) { toast.error("Could not approve all"); }
  };

  const loadStatic = useCallback(async () => {
    try {
      const [s, sc] = await Promise.all([
        axios.get(`${API}/kb/stats`),
        axios.get(`${API}/kb/scopes`),
      ]);
      setStats(s.data); setScopes(sc.data);
    } catch (e) { /* silent */ }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      if (semantic && query.trim()) {
        const { data } = await axios.post(`${API}/kb/search`, {
          query: query.trim(),
          scope: fScope !== "all" ? fScope : undefined,
          vertical: fVertical !== "all" ? fVertical : undefined,
          source_type: fSource !== "all" ? fSource : undefined,
          limit: 40,
        });
        setEntries((data.results || []).map((r) => ({ ...r, _score: r.scores?.final })));
        setTotal((data.results || []).length);
      } else {
        const params = { limit: 60 };
        if (query.trim()) params.q = query.trim();
        if (fScope !== "all") params.scope = fScope;
        if (fVertical !== "all") params.vertical = fVertical;
        if (fSource !== "all") params.source_type = fSource;
        const { data } = await axios.get(`${API}/kb/entries`, { params });
        setEntries((data.entries || []).map((e) => ({
          ...e, vertical: e.metadata?.vertical, tags: e.metadata?.tags,
          confidence: e.metadata?.confidence, pinned: e.metadata?.pinned,
        })));
        setTotal(data.total || 0);
      }
    } catch (e) {
      toast.error("Could not load knowledge entries");
    } finally { setLoading(false); }
  }, [query, semantic, fScope, fVertical, fSource]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadQueueCount(); }, [loadQueueCount]);
  useEffect(() => { if (view === "queue") loadQueue(); }, [view, loadQueue]);
  useEffect(() => { if (view === "library") loadEntries(); }, [view]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(loadEntries, semantic ? 350 : 200);
    return () => clearTimeout(t);
  }, [loadEntries, semantic]);

  const scopeOptions = useMemo(() => {
    if (!scopes) return [];
    const opts = [{ value: "individual:", label: "Individual (private)" }];
    (scopes.projects || []).forEach((p) => opts.push({ value: `project:${p.scope_id}`, label: `Project · ${p.name}` }));
    (scopes.teams || []).forEach((t) => opts.push({ value: `team:${t.scope_id}`, label: `Team · ${t.name}` }));
    if (scopes.organization?.can_write) opts.push({ value: `organization:${scopes.organization.scope_id}`, label: "Organization (everyone)" });
    return opts;
  }, [scopes]);

  const refresh = () => { loadStatic(); loadEntries(); };

  const createEntry = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and content are required"); return; }
    const [scope, scope_id] = form.scope.split(":");
    setSaving(true);
    try {
      await axios.post(`${API}/kb/entries`, {
        scope, scope_id: scope_id || null,
        title: form.title.trim(), body: form.body.trim(),
        content_type: form.content_type, vertical: form.vertical,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Knowledge entry created");
      setCreateOpen(false); setForm(EMPTY_FORM); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Could not create entry");
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/kb/entries/${editing.id}`, {
        title: editing.title, body: editing.body,
        vertical: editing.vertical, content_type: editing.content_type,
        tags: (typeof editing.tagsStr === "string"
          ? editing.tagsStr.split(",").map((t) => t.trim()).filter(Boolean)
          : editing.tags),
      });
      toast.success("Entry updated"); setEditing(null); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not update");
    } finally { setSaving(false); }
  };

  const doAction = async (fn, ok) => {
    try { await fn(); toast.success(ok); refresh(); }
    catch (e) { toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Action failed"); }
  };

  const promote = (e) => {
    const idx = SCOPE_ORDER.indexOf(e.scope);
    const next = SCOPE_ORDER[Math.min(idx + 1, SCOPE_ORDER.length - 1)];
    if (next === e.scope) { toast.info("Already at the top scope"); return; }
    // organization target needs no scope_id (backend defaults); team needs one
    if (next === "team") {
      const t = (scopes?.teams || [])[0];
      if (!t) { toast.error("You are not a member of any team"); return; }
      return doAction(() => axios.patch(`${API}/kb/entries/${e.id}/scope`, { scope: "team", scope_id: t.scope_id }), "Promoted to Team");
    }
    if (next === "project") {
      const p = (scopes?.projects || [])[0];
      if (!p) { toast.error("No project available"); return; }
      return doAction(() => axios.patch(`${API}/kb/entries/${e.id}/scope`, { scope: "project", scope_id: p.scope_id }), "Promoted to Project");
    }
    return doAction(() => axios.patch(`${API}/kb/entries/${e.id}/scope`, { scope: next }), `Promoted to ${SCOPE_CONFIG[next].label}`);
  };

  return (
    <div className="space-y-6" data-testid="knowledge-explorer">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--qn-foreground)] flex items-center gap-2">
            <Library className="w-7 h-7 text-[var(--qn-primary)]" /> Knowledge Base
          </h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1 max-w-2xl">
            Your organization's compounding memory. Every entry makes the next Swarm task faster and smarter.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-1.5" data-testid="kb-new-entry-btn">
          <Plus className="w-4 h-4" /> New Entry
        </Button>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] w-fit" data-testid="kb-view-toggle">
        <button
          onClick={() => setView("library")}
          data-testid="kb-view-library"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "library" ? "bg-[var(--qn-primary)] text-white" : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
        >
          <Library className="w-4 h-4" /> Library
        </button>
        <button
          onClick={() => setView("queue")}
          data-testid="kb-view-queue"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "queue" ? "bg-[var(--qn-primary)] text-white" : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
        >
          <Inbox className="w-4 h-4" /> Review Queue
          {queueCount > 0 && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-500 text-white" data-testid="kb-queue-badge">{queueCount}</span>
          )}
        </button>
        <button
          onClick={() => setView("graph")}
          data-testid="kb-view-graph"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "graph" ? "bg-[var(--qn-primary)] text-white" : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
        >
          <Network className="w-4 h-4" /> Graph
        </button>
        <button
          onClick={() => setView("insights")}
          data-testid="kb-view-insights"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "insights" ? "bg-[var(--qn-primary)] text-white" : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
        >
          <BarChart3 className="w-4 h-4" /> Insights
        </button>
      </div>

      {view === "graph" && <KnowledgeGraph />}
      {view === "insights" && <KnowledgeAnalytics isAdmin={scopes?.organization?.can_write} />}

      {/* Review Queue */}
      {view === "queue" && (
        <div className="space-y-3" data-testid="kb-queue">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--qn-foreground-muted)]">
              Artifacts extracted from task outputs, awaiting your review.
            </p>
            {queue.length > 0 && (
              <Button size="sm" onClick={approveAll} className="gap-1.5" data-testid="kb-approve-all">
                <ClipboardCheck className="w-4 h-4" /> Approve all
              </Button>
            )}
          </div>
          {queueLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : queue.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="kb-queue-empty">
              <Inbox className="w-12 h-12 text-slate-300 mb-3" />
              <div className="font-semibold text-[var(--qn-foreground)]">Review queue is empty</div>
              <p className="text-sm text-[var(--qn-foreground-muted)] max-w-sm mt-1">
                Use "Extract knowledge" on a completed task to auto-capture findings here for review.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="kb-queue-list">
              {queue.map((e) => (
                <Card key={e.id} className="border-amber-200 bg-amber-50/30" data-testid={`kb-queue-${e.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-[var(--qn-foreground)]">{e.title}</div>
                      <ScopeBadge scope={e.scope} />
                    </div>
                    <p className="text-sm text-[var(--qn-foreground-muted)] line-clamp-3">{e.body || e.summary}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{verticalLabel(e.metadata?.vertical)}</Badge>
                      <Badge variant="outline" className="text-[10px]">{SOURCE_TYPE_LABEL[e.source_type] || e.source_type}</Badge>
                      {(e.metadata?.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] text-slate-500 inline-flex items-center gap-0.5"><Tag className="w-2.5 h-2.5" />{t}</span>
                      ))}
                    </div>
                    {e.dedup_candidate && (
                      <div className="flex items-center justify-between gap-2 text-[11px] bg-white border border-amber-200 rounded-md px-2 py-1.5">
                        <span className="text-amber-700 flex items-center gap-1">
                          <GitMerge className="w-3 h-3" /> Similar to "{e.dedup_candidate.title}" ({Math.round(e.dedup_candidate.similarity * 100)}%)
                        </span>
                        <button
                          className="text-[var(--qn-primary)] font-medium hover:underline"
                          data-testid={`kb-queue-merge-${e.id}`}
                          onClick={() => queueAction(() => axios.post(`${API}/kb/queue/${e.id}/merge`, { target_id: e.dedup_candidate.id }), "Merged")}
                        >Merge</button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <Button size="sm" className="gap-1.5" data-testid={`kb-queue-approve-${e.id}`}
                        onClick={() => queueAction(() => axios.post(`${API}/kb/queue/${e.id}/approve`), "Approved")}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5" data-testid={`kb-queue-reject-${e.id}`}
                        onClick={() => queueAction(() => axios.post(`${API}/kb/queue/${e.id}/reject`), "Rejected")}>
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "library" && (<>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kb-stats">
          <StatTile label="Total" value={stats.total} icon={Layers} />
          {SCOPE_ORDER.map((s) => (
            <StatTile key={s} label={SCOPE_CONFIG[s].label} value={stats.by_scope?.[s] ?? 0} dot={SCOPE_CONFIG[s].dot} />
          ))}
        </div>
      )}

      {/* Search + filters */}
      <Card className="border-[var(--qn-border)]">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={semantic ? "Ask in natural language…" : "Filter by keyword…"}
                className="pl-9"
                data-testid="kb-search-input"
              />
            </div>
            <Button
              variant={semantic ? "default" : "outline"}
              onClick={() => setSemantic((s) => !s)}
              className="gap-1.5"
              data-testid="kb-semantic-toggle"
            >
              <Sparkles className="w-4 h-4" /> Semantic
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterSelect value={fScope} onChange={setFScope} testid="kb-filter-scope" placeholder="Scope"
              options={[{ value: "all", label: "All scopes" }, ...SCOPE_ORDER.map((s) => ({ value: s, label: SCOPE_CONFIG[s].label }))]} />
            <FilterSelect value={fVertical} onChange={setFVertical} testid="kb-filter-vertical" placeholder="Vertical"
              options={[{ value: "all", label: "All verticals" }, ...VERTICALS]} />
            <FilterSelect value={fSource} onChange={setFSource} testid="kb-filter-source" placeholder="Source"
              options={[{ value: "all", label: "All sources" }, ...Object.entries(SOURCE_TYPE_LABEL).map(([v, l]) => ({ value: v, label: l }))]} />
            <div className="ml-auto text-xs text-[var(--qn-foreground-muted)] self-center">
              {loading ? "Loading…" : `${total} ${semantic ? "matches" : "entries"}`}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entry list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="kb-entry-list">
          <AnimatePresence>
            {entries.map((e) => (
              <motion.div key={e.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Card className="border-[var(--qn-border)] hover:shadow-md transition-shadow" data-testid={`kb-entry-${e.id}`}>
                  <CardContent className="p-4 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <button className="text-left" onClick={() => setViewing(e)}>
                        <div className="font-semibold text-[var(--qn-foreground)] flex items-center gap-1.5">
                          {e.pinned && <Pin className="w-3.5 h-3.5 text-amber-500" />}
                          {e.title}
                        </div>
                      </button>
                      <ScopeBadge scope={e.scope} />
                    </div>
                    <p className="text-sm text-[var(--qn-foreground-muted)] line-clamp-2">{e.summary}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{verticalLabel(e.vertical)}</Badge>
                      <Badge variant="outline" className="text-[10px]">{SOURCE_TYPE_LABEL[e.source_type] || e.source_type}</Badge>
                      {typeof e.confidence === "number" && (
                        <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                          <BadgeCheck className="w-3 h-3" /> {Math.round(e.confidence * 100)}%
                        </span>
                      )}
                      {typeof e._score === "number" && (
                        <span className="text-[10px] font-mono text-emerald-600" title="Relevance score">
                          {e._score.toFixed(2)}
                        </span>
                      )}
                      {(e.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                          <Tag className="w-2.5 h-2.5" />{t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 pt-1">
                      <IconBtn title="Edit" testid={`kb-edit-${e.id}`} onClick={() => setEditing({ ...e, tagsStr: (e.tags || []).join(", ") })}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Promote scope" testid={`kb-promote-${e.id}`} onClick={() => promote(e)}><ArrowUpNarrowWide className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Validate (boost confidence)" testid={`kb-validate-${e.id}`} onClick={() => doAction(() => axios.post(`${API}/kb/entries/${e.id}/validate`), "Validated")}><ShieldCheck className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Flag as inaccurate" testid={`kb-flag-${e.id}`} onClick={() => doAction(() => axios.post(`${API}/kb/entries/${e.id}/flag`, { reason: "Flagged from explorer" }), "Flagged for review")}><Flag className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title={e.pinned ? "Unpin" : "Pin"} testid={`kb-pin-${e.id}`} onClick={() => doAction(() => axios.patch(`${API}/kb/entries/${e.id}`, { pinned: !e.pinned }), e.pinned ? "Unpinned" : "Pinned")}>{e.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</IconBtn>
                      <IconBtn title="Archive" testid={`kb-archive-${e.id}`} onClick={() => doAction(() => axios.delete(`${API}/kb/entries/${e.id}`), "Archived")}><Archive className="w-3.5 h-3.5" /></IconBtn>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
      </>)}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="kb-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Brain className="w-5 h-5 text-[var(--qn-primary)]" /> New Knowledge Entry</DialogTitle>
            <DialogDescription>Add a fact, insight, preference or procedure to the memory layer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kb-form-title" />
            <Textarea placeholder="Knowledge content…" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="kb-form-body" />
            <div className="grid grid-cols-2 gap-2">
              <LabeledSelect label="Scope" value={form.scope} onChange={(v) => setForm({ ...form, scope: v })} options={scopeOptions} testid="kb-form-scope" />
              <LabeledSelect label="Vertical" value={form.vertical} onChange={(v) => setForm({ ...form, vertical: v })} options={VERTICALS} testid="kb-form-vertical" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <LabeledSelect label="Type" value={form.content_type} onChange={(v) => setForm({ ...form, content_type: v })} options={CONTENT_TYPES} testid="kb-form-type" />
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Tags (comma separated)</label>
                <Input placeholder="pricing, q3" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="kb-form-tags" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createEntry} disabled={saving} data-testid="kb-create-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-xl" data-testid="kb-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{viewing.title}</DialogTitle>
                <DialogDescription className="flex items-center gap-2 pt-1">
                  <ScopeBadge scope={viewing.scope} />
                  <Badge variant="outline" className="text-[10px]">{verticalLabel(viewing.vertical)}</Badge>
                  <Badge variant="outline" className="text-[10px]">{viewing.content_type}</Badge>
                </DialogDescription>
              </DialogHeader>
              <div className="text-sm text-[var(--qn-foreground)] whitespace-pre-wrap max-h-[50vh] overflow-y-auto">{viewing.body || viewing.summary}</div>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {(viewing.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="kb-edit-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle>Edit Entry</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="kb-edit-title" />
                <Textarea rows={5} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="kb-edit-body" />
                <div className="grid grid-cols-2 gap-2">
                  <LabeledSelect label="Vertical" value={editing.vertical} onChange={(v) => setEditing({ ...editing, vertical: v })} options={VERTICALS} testid="kb-edit-vertical" />
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Tags</label>
                    <Input value={editing.tagsStr} onChange={(e) => setEditing({ ...editing, tagsStr: e.target.value })} data-testid="kb-edit-tags" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} disabled={saving} data-testid="kb-edit-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, dot }) {
  return (
    <Card className="border-[var(--qn-border)]">
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)]">
          {Icon ? <Icon className="w-3.5 h-3.5" /> : <span className={`w-2 h-2 rounded-full ${dot}`} />}
          {label}
        </div>
        <div className="text-2xl font-bold text-[var(--qn-foreground)] mt-1 font-mono">{value}</div>
      </CardContent>
    </Card>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[140px] h-9" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function LabeledSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-slate-500">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="z-[70]">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function IconBtn({ children, title, onClick, testid }) {
  return (
    <button title={title} onClick={onClick} data-testid={testid}
      className="w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:text-[var(--qn-primary)] hover:bg-[var(--qn-surface-hover)] transition-colors">
      {children}
    </button>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="kb-empty">
      <Library className="w-12 h-12 text-slate-300 mb-3" />
      <div className="font-semibold text-[var(--qn-foreground)]">No knowledge yet</div>
      <p className="text-sm text-[var(--qn-foreground-muted)] max-w-sm mt-1">
        Add your first entry, or use the "Save to Knowledge Base" button on any task output to start building your compounding memory.
      </p>
      <Button className="mt-4 gap-1.5" onClick={onCreate}><Plus className="w-4 h-4" /> New Entry</Button>
    </div>
  );
}
